import { put, select, call } from 'redux-saga/effects';
import { id, Id } from '@/types/index';
import { IUser, IUserInvite, SsoUser } from '@/types/user';
import { ICard } from '@/types/billing';
import { IAccount, IAccountStats } from '@/types/account';
import { showErrorToast } from '../../components/Toastr/actions';
// FIX: Removed 'all', retained 'call', 'put', 'select'
import { getCurrentUser, getCurrentAccount } from '../../features/Auth/selectors';
import { setCurrentUser } from '../../features/Auth/authSlice';
import { addEntities } from '../../models/actions';
import { ICompanyTasks } from '../../types/unassignDialog';
import { findAllValues } from '../../models/selectors';
import { push } from '@/App/router/actions';
import { internalApi } from '@/utils/request';

// --- LEGACY NETWORK ADAPTERS ---
// These adapters map the legacy object/string arguments to standard Axios calls
const parseArgs = (args: any) => (typeof args === 'string' ? { url: args } : args);

const get = async (args: any) => {
  const { url, ...config } = parseArgs(args);
  const res = await internalApi.get(url, config);
  return res.data !== undefined ? res.data : res;
};

const post = async (args: any) => {
  const { url, data, ...config } = parseArgs(args);
  const res = await internalApi.post(url, data, config);
  return res.data !== undefined ? res.data : res;
};

const patch = async (args: any) => {
  const { url, data, ...config } = parseArgs(args);
  const res = await internalApi.patch(url, data, config);
  return res.data !== undefined ? res.data : res;
};

const destroy = async (url: string) => {
  const res = await internalApi.delete(url);
  return res.data !== undefined ? res.data : res;
};

export const defaultUser = {
  analytics: {
    companies: 0,
    jobs: 0,
  },
  createdAt: '2017-02-14T13:01:36.103Z',
};
export function* pauseAccount(accountId: Id) {
  try {
    return yield post({ url: `/accounts/${accountId}/pause` });
  } catch (err) {
    if (err instanceof Error) {
      console.log(err.message);
    }
    return false;
  }
}
export function* activateAccount(accountId: Id, restoreActivity: boolean = true) {
  const params = { restoreActivity };
  try {
    return yield post({ url: `/accounts/${accountId}/activate`, params });
  } catch (err) {
    const isCardIdProblem = err instanceof Error && /Card id was not found/.test(err.message);
    if (isCardIdProblem) {
      yield put(push('/settings/billing'));
      yield put(
        showErrorToast('Current primary payment method is not valid. Please, edit or change your payment card.'),
      );
    } else {
      if (err instanceof Error) {
        yield put(showErrorToast(err.message));
      }
    }
    return false;
  }
}
export function* fetchTeam(accountId: id) {
  const team: IUser[] = yield get(`/accounts/${accountId}/users`);
  return team.map((t) => Object.assign({}, t, defaultUser));
}
export function* createAccountLocationRelations(accountId: string, locationIds: string[]) {
  const data = locationIds.map((id) => ({
    accountId,
    locationId: id,
  }));
  const createdRelations = yield post({ url: '/accountLocations', data });
  return createdRelations;
}
export function* patchPassword(currentPassword: string, newPassword: string) {
  const data = {
    currentPassword,
    newPassword,
  };
  return yield post({ url: '/users/changePassword', data });
}
export function* createCard(accountId: string, data: Partial<ICard>) {
  const createdCard = yield post({ url: `/accounts/${accountId}/cards`, data });
  return createdCard;
}
export function* assignLocationToCard(locationId: string, cardId: string) {
  const createdRelation = yield post({ url: '/cardLocations', data: { locationId, cardId } });
  return createdRelation;
}
export function* patchCardLocation(id: string, data: { cardId?: string; locationId?: string }) {
  const patchedRef = yield patch({ url: `/cardLocations/${id}`, data });
  return patchedRef;
}
export function* deleteCard(cardId: string) {
  const deletedCard = yield destroy(`/cards/${cardId}`);
  return deletedCard;
}
export function* patchCard(cardId: string, data: { defaultCard: boolean }) {
  if (!Object.keys(data).length) return;
  const patchedCard = yield patch({ url: `/cards/${cardId}`, data });
  return patchedCard;
}
export function* patchAccount(accountId: id, data: Partial<IAccount>) {
  const patchedAccount = yield patch({ url: `/accounts/${accountId}`, data });
  return patchedAccount;
}
export function* deleteAccount(accountId: string) {
  const deletedAccount = yield post(`/accounts/${accountId}/removeAccount`);
  return deletedAccount;
}
export function* inviteTeamMembers(users: IUserInvite[]) {
  const data = users
    .filter((u) => u.email)
    .map((u) => ({
      email: u.email,
      permission: u.permission,
      hostname: location.origin,
      firstname: (u.fullName || '').trim().split(/\s+/)[0],
      lastname: (u.fullName || '').trim().split(/\s+/)[1],
    }));
  const invitedUsers = yield post({ url: '/accounts/inviteUsers', data });
  return invitedUsers;
}
export function* fetchAccountStats(accountId: string) {
  const stats: IAccountStats = yield get({
    url: '/accounts/stats',
    params: { accountId: accountId, cache: 60 * 60 * 6 },
  });
  return stats;
}
export function* checkIsAccountEmailExists(email: string) {
  const isExists: boolean = yield get({ url: '/accounts/checkEmail', params: { email } });
  return isExists;
}
export function* patchUser(userId: id, data: any) {
  // FIX: Using sequential calls to avoid "all" (not in saga 0.12)
  // and array yield issues.
  const patchedUser = yield call(patch, { url: `/users/${userId}`, data });
  const currentUser = yield select(getCurrentUser);
  if (patchedUser.id === currentUser.id) {
    yield put(setCurrentUser(patchedUser as any));
  }
  yield put(addEntities({ user: { [patchedUser.id]: patchedUser } }));
  return patchedUser;
}
export function* makePayment(cardId: id, amount: string) {
  return yield post({ url: `/cards/${cardId}/charge`, data: { amount } });
}
export function* deleteUserFromAccount(accountId: id, userId: id) {
  return yield destroy(`/accounts/${accountId}/users/${userId}`);
}
export function* blockAccount(accountId: string) {
  return yield post({ url: `/accounts/${accountId}/block` });
}
export function* unassignUsersTasks(userId: string, values: ICompanyTasks) {
  const jobRefs: any[] = yield select(findAllValues('companyJobPosition'));
  const companyIds = Object.keys(values);
  const data = companyIds.reduce((data, cid) => {
    return {
      ...data,
      [cid]: {
        company: [{ id: cid }],
        ...values[cid],
        jobPosition: (values[cid].jobPosition || []).concat(
          jobRefs
            .filter(
              (cj) =>
                cj.companyId === cid &&
                (!values[cid].jobPosition || !values[cid].jobPosition.find((j) => j.id === cj.jobPositionId)),
            )
            .map((cj) => ({ id: cj.jobPositionId })),
        ),
      },
    };
  }, {});
  const res = yield post({
    url: `/users/${userId}/delegateTasks`,
    data,
  });
  return res;
}
export function* getInvoice(transactionId: id) {
  return yield get({ url: `/transactions/${transactionId}/getInvoice` });
}
export function* uploadCandidate(file: File) {
  const account: IAccount = yield select(getCurrentAccount);
  const data = new FormData();
  data.append('sourceFile', file);
  const candidate = yield post({ url: `/accounts/${account.id}/uploadCandidates`, data });
  return candidate;
}
export function* getInvoiceFile(url: string) {
  return yield get({ url, responseType: 'arraybuffer', cache: 60 * 60 * 6 });
}
export function* restore(email: string) {
  const response = yield post({ url: `/accounts/restore`, data: { email } });
  return response;
}